import React from 'react'

export interface SkeletonProps {
  className?: string
  /** Whether this is a circular skeleton (for avatars, buttons, etc.) */
  circular?: boolean
  /** Height in Tailwind units (e.g., "4", "6", "12") */
  height?: string
  /** Width in Tailwind units (e.g., "24", "32", "full") */
  width?: string
  /** Whether to animate the skeleton */
  animate?: boolean
}

export const Skeleton: React.FC<SkeletonProps> = ({ 
  className = '', 
  circular = false, 
  height = '4', 
  width = 'full',
  animate = true 
}) => {
  const baseClasses = `bg-black/5 ${animate ? 'animate-pulse' : ''}`
  const shapeClasses = circular ? 'rounded-full' : 'rounded-lg'
  const dimensionClasses = `h-${height} w-${width}`
  
  return (
    <div 
      className={`${baseClasses} ${shapeClasses} ${dimensionClasses} ${className}`}
      aria-hidden="true"
    />
  )
}

/**
 * Pre-configured skeleton components for common patterns
 */
export const SkeletonText: React.FC<{ className?: string; lines?: number }> = ({ 
  className = '', 
  lines = 1 
}) => {
  if (lines === 1) {
    return <Skeleton className={className} height="4" />
  }
  
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton 
          key={i} 
          height="4" 
          width={i === lines - 1 ? '3/4' : 'full'} 
        />
      ))}
    </div>
  )
}

export const SkeletonButton: React.FC<{ className?: string; size?: 'sm' | 'md' | 'lg' }> = ({ 
  className = '', 
  size = 'md' 
}) => {
  const heights = { sm: '8', md: '10', lg: '12' }
  const widths = { sm: '16', md: '20', lg: '24' }
  
  return (
    <Skeleton 
      className={className} 
      height={heights[size]} 
      width={widths[size]} 
    />
  )
}

export const SkeletonAvatar: React.FC<{ className?: string; size?: 'sm' | 'md' | 'lg' }> = ({ 
  className = '', 
  size = 'md' 
}) => {
  const sizes = { sm: '8', md: '10', lg: '12' }
  const sizeClass = sizes[size]
  
  return (
    <Skeleton 
      className={className} 
      height={sizeClass} 
      width={sizeClass} 
      circular 
    />
  )
}

export const SkeletonCard: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`rounded-xl border border-black/5 bg-white p-5 space-y-3 ${className}`}>
      <div className="flex items-start justify-between">
        <div className="space-y-2 flex-1">
          <Skeleton height="4" width="3/4" />
          <Skeleton height="3" width="1/2" />
        </div>
        <SkeletonButton size="sm" />
      </div>
      <SkeletonText lines={2} />
    </div>
  )
}

export default Skeleton
